<template>
  <el-form
    @submit.prevent="validateSubmit('accountTemplateForm')"
    :model="template"
    :rules="rules"
    label-position="top"
    ref="accountTemplateForm"
    novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            faIcon="save"
            @click.prevent="validateSubmit('accountTemplateForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
            :title="$t('actions.clearButton')"
            isDanger
            faIcon="trash"
            @click.prevent="clearForm('accountTemplateForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('section') }}</h1>

      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <el-form-item :label="t('enName')" prop="enName" class="input-label col-span-2">
          <el-input
            id="en-name"
            v-model="template.enName"
            placeholder=""
            maxlength="255"
            clearable
            required
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('arName')" prop="arName" class="input-label col-span-2">
          <el-input
            id="ar-name"
            v-model="template.arName"
            placeholder=""
            maxlength="255"
            clearable
            required
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('code')" prop="code" class="input-label">
          <el-input-number
            id="code"
            v-model="template.code"
            :placeholder="t('code')"
            :controls=false
            clearable
            class="w-full"
          >
          </el-input-number>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-7 gap-4">
        <el-form-item :label="t('mainCategory')" class="input-label col-span-2" prop="accountCategory">
          <el-select
            id="account-category"
            v-model="template.accountCategory"
            :trigger-on-focus="true"
            class="w-full"
            placeholder=""
            @change="onAccountCategoryChange"
            filterable
          >
            <el-option
              v-for="option in accountCategories"
              :key="option"
              :label="t(option)"
              :value="option"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('belongsToAccount')" class="input-label col-span-2" prop="parentAccountID">
          <el-select
            id="parent-account"
            v-model="template.parentAccountID"
            :trigger-on-focus="false"
            value-key="name"
            class="w-full"
            filterable
            placeholder=""
            remote
            :remote-method="searchAccounts"
            clearable
          >
            <el-option
              v-if="selectedParentAccount"
              :key="selectedParentAccount.id"
              :label="$ctx.locale === 'en' ? selectedParentAccount.enName : selectedParentAccount.arName"
              :value="selectedParentAccount.id"
            ></el-option>

            <el-option
              v-for="option in accounts"
              :key="option.id"
              :label="$ctx.locale === 'en' ? option.enName : option.arName"
              :value="option.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('subcategory')" class="input-label col-span-2" prop="subcategory">
          <el-select
            id="account-subcategory"
            v-model="template.accountSubcategory"
            :trigger-on-focus="true"
            class="w-full"
            filterable
            clearable
            placeholder=""
            :disabled="!template.accountCategory"
          >
            <el-option
              v-for="option in currentSubcategories"
              :key="option"
              :label="t(option)"
              :value="option"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('balance')" class="input-label" prop="balance">
          <el-select
            id="balance"
            v-model="template.balance"
            :trigger-on-focus="true"
            class="w-full"
            placeholder=""
            filterable
          >
            <el-option
              :label="t('debit')"
              value="debit"
            ></el-option>
            <el-option
              :label="t('credit')"
              value="credit"
            ></el-option>
          </el-select>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 gap-4">
        <el-form-item :label="t('enDescription')" prop="enDescription" class="input-label">
          <el-input
            id="en-description"
            v-model="template.enDescription"
            maxlength="255"
            clearable
          >
          </el-input>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 gap-4">
        <el-form-item :label="t('arDescription')" prop="arDescription" class="input-label">
          <el-input
            id="ar-description"
            v-model="template.arDescription"
            maxlength="255"
            clearable
          >
          </el-input>
        </el-form-item>
      </div>

      <div class="flex flex-wrap gap-6 mt-4">
        <el-form-item prop="isOnlyForCurrentLocation" class="mb-0">
          <el-checkbox
            id="is-only-for-current-location"
            v-model="template.isOnlyForCurrentLocation"
            @change="onOnlyForCurrentLocationChange"
          >
            {{ t('onlyForThisLocation') }}
          </el-checkbox>
        </el-form-item>

        <el-form-item prop="isTransactional" class="mb-0">
          <el-checkbox
            id="is-transactional"
            v-model="template.isTransactional"
            @change="onTransactionalChange"
          >
            {{ t('transactional') }}
          </el-checkbox>
        </el-form-item>

        <el-form-item prop="isCashierRegister" class="mb-0">
          <el-checkbox
            id="is-cashier-register"
            v-model="template.isCashierRegister"
            :disabled="!template.isTransactional"
          >
            {{ t('cashierRegister') }}
          </el-checkbox>
        </el-form-item>
      </div>
    </input-section>
  </el-form>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import AlertBadge from '@/components/AlertBadge.vue'

import { ElForm } from 'element-plus'
import { Route } from '@/plugins/navigation'

// models
import { AccountTemplate, accountTemplateI18nMessages } from '@/models/accounting/AccountTemplate'
import { Account } from '@/models/accounting/Account'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'account-template-form',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...accountTemplateI18nMessages.en,
          section: 'Account Info',
          mainCategory: 'Main Category',
          subcategory: 'Subcategory',
          belongsToAccount: 'Belongs to Account',
          sharedAccount: 'Shared Account',
          onlyForThisLocation: 'Only for this location',
          cashierRegister: 'Cashier Register',
          transactional: 'Transactional',
          assets: "Assets",
          liabilities: "Liabilities",
          equity: "Equity",
          revenue: "Revenue",
          expenses: "Expenses",
          "current-assets": "Current Asset",
          "fixed-assets": "Fixed Asset (PPE)",
          "employee-receivable": "Employee Receivable",
          "contra-assets": "Contra-Asset",
          "current-liabilities": "Current Liability",
          "accrued-expense": "Accrued Expense",
          "long-term-liabilities": "Long-term Liability",
          "owner-equity": "Owner Equity",
          "contra-equity": "Contra-Equity",
          "operating-revenue": "Operating Revenue",
          "contra-revenue": "Contra-Revenue",
          "cogs": "COGS",
          "adjustment": "Adjustment",
          "write-off": "Write-off",
          "contra-cogs": "Contra-COGS",
          "payroll": "Payroll",
          "operating-expenses": "Operating Expenses",
          "selling-expenses": "Selling Expense",
          "depreciation": "Depreciation",
          "other-expenses": "Other Expenses"
        },
        ar: {
          ...accountTemplateI18nMessages.ar,
          section: 'معلومات الحساب',
          mainCategory: 'الفئة الرئيسية',
          subcategory: 'الفئة الفرعية',
          belongsToAccount: 'تابع للحساب',
          sharedAccount: 'حساب مشترك',
          onlyForThisLocation: 'للموقع الحالي فقط',
          cashierRegister: 'حساب صندوق',
          transactional: 'حساب معاملات',
          assets: "الممتلكات",
          liabilities: "الديون",
          equity: "حقوق الملكية",
          revenue: "الوارد",
          expenses: "المصاريف",
          "current-assets": "أصول متداولة",
          "fixed-assets": "أصول ثابتة",
          "employee-receivable": "سلفة للموظفين",
          "contra-assets": "أصول مقابلة",
          "current-liabilities": "خصوم متداولة",
          "accrued-expense": "مصاريف مستحقة",
          "long-term-liabilities": "خصوم طويلة الأجل",
          "owner-equity": "رأس مال المالك",
          "contra-equity": "حقوق ملكية مقابلة",
          "cogs": "تكلفة المبيعات",
          "adjustment": "تعديلات",
          "write-off": "شطب",
          "contra-cogs": "تكلفة مبيعات مقابلة",
          "payroll": "رواتب وأجور",
          "operating-revenue": "إيرادات تشغيلية",
          "contra-revenue": "إيرادات مقابلة",
          "operating-expenses": "مصاريف تشغيلية",
          "selling-expenses": "مصاريف بيع",
          "depreciation": "اندثار",
          "other-expenses": "مصاريف أخرى"
        }
      },
      unescape: 'global'
    })

    return { t }
  },

  components: {
    AlertBadge,
    HorizontalButton,
    InputSection
  },

  props: {
    initTemplate: {
      type: AccountTemplate,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    const accounts = new Array<Account>()
    const accountCategories = ['assets', 'liabilities', 'equity', 'revenue', 'expenses']

    const subcategoriesMap: Record<string, string[]> = {
      "assets": [
        "current-assets",
        "fixed-assets",
        "employee-receivable",
        "contra-assets",
      ],
      "liabilities": [
        "current-liabilities",
        "accrued-expense",
        "long-term-liabilities",
      ],
      "equity": [
        "owner-equity",
        "contra-equity",
        "equity",
      ],
      "revenue": [
        "operating-revenue",
        "contra-revenue",
      ],
      "expenses": [
        "cogs",
        "adjustment",
        "write-off",
        "contra-cogs",
        "payroll",
        "operating-expenses",
        "selling-expenses",
        "depreciation",
        "other-expenses",
      ],
    }

    return {
      template: this.initTemplate,
      accounts,
      error: { title: '', body: '' },
      accountCategories,
      subcategoriesMap,
      selectedParentAccount: null as Account | null,
      rules: {
        enName: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', { len: 255 }),
            trigger: 'blur'
          }
        ],
        arName: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', { len: 255 }),
            trigger: 'blur'
          }
        ],
        accountCategory: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ],
        balance: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ]
      }
    }
  },

  computed: {
    currentSubcategories(): string[] {
      if (!this.template.accountCategory) {
        return []
      }
      return this.subcategoriesMap[this.template.accountCategory] || []
    }
  },

  methods: {
    async searchAccounts(query: string) {
      try {
        const url = `${Account.ENDPOINT}?q=${query}&account-category=${this.template.accountCategory?.toLowerCase()}`
        this.accounts = await this.$http.get<Account[]>(url)
      } catch (error) {
        this.$alertModal.showDanger({ title: error.title })
        this.accounts = []
      }
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        }

        const resp = await this.submitAction(this.template)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
          this.$goTo(Route.ACCOUNTING_ACCOUNTS)
        }
        return true
      })
    },

    onAccountCategoryChange() {
      this.template.parentAccountID = ''
      this.template.accountSubcategory = undefined
      this.selectedParentAccount = null
      this.searchAccounts('')

      // Auto-set balance based on account category
      // Assets, Expenses -> Debit
      // Liabilities, Equity, Revenue -> Credit
      if (this.template.accountCategory === 'assets' || this.template.accountCategory === 'expenses') {
        this.template.balance = 'debit'
      } else if (this.template.accountCategory === 'liabilities' || this.template.accountCategory === 'equity' || this.template.accountCategory === 'revenue') {
        this.template.balance = 'credit'
      }
    },

    onOnlyForCurrentLocationChange(value: boolean) {
      this.template.isSharedAccount = !value;
    },

    onTransactionalChange(value: boolean) {
      if (!value && this.template.isCashierRegister) {
        this.template.isCashierRegister = false
      }
    }
  }
})
</script>

<style scoped>
h1,
h2,
h3 {
  @apply text-lg font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
